import React, { useState } from 'react';
import { X, Save } from 'lucide-react';
import api from '../../services/api';
import RecurrencePicker from './RecurrencePicker';

function toDatetimeLocal(date) {
  const d = new Date(date);
  d.setMinutes(d.getMinutes() - d.getTimezoneOffset());
  return d.toISOString().slice(0, 16);
}

// Edit a still-scheduled CrossPost before it fires — caption, schedule time,
// and recurrence. Media/targets aren't editable here (out of scope for this
// pass); re-create the post instead if those need to change.
export default function CrossPostEditModal({ post, onClose, onSaved }) {
  const [caption, setCaption] = useState(post.caption || '');
  const [scheduledAt, setScheduledAt] = useState(toDatetimeLocal(post.scheduledAt));
  const [recurrence, setRecurrence] = useState(post.recurrence?.frequency !== 'none' ? post.recurrence : { frequency: 'none' });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    if (!caption.trim() && !post.media?.length && !post.mediaUrl) {
      setError('A post needs media and/or a caption');
      return;
    }
    if (new Date(scheduledAt).getTime() <= Date.now()) {
      setError('Scheduled time must be in the future');
      return;
    }
    if (recurrence.frequency === 'weekly' && !recurrence.daysOfWeek?.length) {
      setError('Pick at least one day of the week to repeat on');
      return;
    }
    if (recurrence.frequency !== 'none' && recurrence.endType === 'on' && !recurrence.endDate) {
      setError('Pick an end date for the recurrence');
      return;
    }
    setSaving(true);
    setError('');
    try {
      await api.patch(`/social/cross-posts/${post._id}`, {
        caption,
        scheduledAt: new Date(scheduledAt).toISOString(),
        recurrence: {
          ...recurrence,
          endDate: recurrence.endType === 'on' ? new Date(recurrence.endDate).toISOString() : undefined,
        },
      });
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save changes');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-end sm:items-center justify-center z-[60] sm:p-4" onClick={onClose}>
      <div className="bg-white rounded-t-lg sm:rounded-lg shadow-[0_2px_8px_rgba(0,0,0,0.16)] w-full max-w-md max-h-[100dvh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-[#E5E5E5]">
          <h3 className="text-[16px] font-bold text-[#2E2E2E]">Edit Scheduled Post</h3>
          <button onClick={onClose} className="text-[#5C5C5C] hover:text-[#2E2E2E]">
            <X size={18} />
          </button>
        </div>
        <div className="p-4 sm:p-6 space-y-3">
          {error && <div className="text-[13px] text-[#BA0517]">{error}</div>}

          <textarea
            value={caption}
            onChange={(e) => setCaption(e.target.value)}
            placeholder="Write a caption…"
            rows={4}
            className="w-full border border-[#E5E5E5] rounded-[4px] px-3 py-2 text-[13px] resize-none focus:outline-none focus:border-[#0176D3]"
          />

          {(post.media?.length > 0 ? post.media : post.mediaUrl ? [{ url: post.mediaUrl, mediaType: post.mediaType }] : []).length > 0 && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-1">
              {(post.media?.length > 0 ? post.media : [{ url: post.mediaUrl, mediaType: post.mediaType }]).map((m, i) => (
                m.mediaType === 'video' ? (
                  <video key={i} src={m.url} className="w-full max-h-32 object-cover rounded-[4px]" controls />
                ) : (
                  <img key={i} src={m.url} alt="" className="w-full max-h-32 object-cover rounded-[4px]" />
                )
              ))}
            </div>
          )}
          <div className="text-[11px] text-[#5C5C5C]">Media and targets can't be changed here — delete and re-create the post instead.</div>

          <input
            type="datetime-local"
            value={scheduledAt}
            onChange={(e) => setScheduledAt(e.target.value)}
            min={new Date(Date.now() + 60000).toISOString().slice(0, 16)}
            className="border border-[#E5E5E5] rounded-[4px] px-3 py-1.5 text-[13px] focus:outline-none focus:border-[#0176D3]"
          />

          <RecurrencePicker onChange={setRecurrence} initial={post.recurrence} />

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-[#E5E5E5]">
            <button onClick={onClose} className="h-8 px-3 rounded bg-white border border-[#C9C9C9] text-[13px] font-semibold text-[#0176D3] hover:bg-[#F3F3F3]">
              Cancel
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="flex items-center gap-1 bg-[#0176D3] hover:bg-[#014486] text-white text-[13px] font-semibold h-8 px-4 rounded disabled:opacity-50 whitespace-nowrap"
            >
              <Save size={14} />
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
